import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  addEdge,
  useReactFlow,
  MarkerType,
  getNodesBounds,
  getViewportForBounds,
  type Connection,
  type Edge,
  type Node,
  type NodeTypes,
  type EdgeTypes,
  BaseEdge,
  getBezierPath,
  type EdgeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { toPng } from 'html-to-image';
import { toast } from 'sonner';
import WorkflowHeader from '../components/orbi/WorkflowHeader';
import WorkflowCanvasTools, { type WorkflowTool } from '../components/orbi/WorkflowCanvasTools';
import PlanNode, {
  type PlanNodeData,
  type PlanNodeType,
  NODE_TYPE_LABEL,
} from '../components/orbi/PlanNode';
import NoteNode, { type NoteNodeData } from '../components/orbi/NoteNode';
import ChatComposer, { type ChatComposerMode } from '../components/orbi/ChatComposer';
import PlanImageLightbox, { downloadImage } from '../components/orbi/PlanImageLightbox';
import { useAuth } from '../contexts/AuthContext';
import { PLANBASE_CONFIG } from '../config';
import { publishPlan, type PlanverseVisibility } from '../lib/planverse';
import { useWorkflowDraft, newDraftId } from '../hooks/useWorkflowDraft';

const nodeTypes: NodeTypes = { plan: PlanNode, note: NoteNode };

/** Thicker orange bezier — matches Figma's connection stroke. */
function OrangeEdge(props: EdgeProps) {
  const [path] = getBezierPath({
    sourceX: props.sourceX,
    sourceY: props.sourceY,
    sourcePosition: props.sourcePosition,
    targetX: props.targetX,
    targetY: props.targetY,
    targetPosition: props.targetPosition,
  });
  return (
    <BaseEdge
      id={props.id}
      path={path}
      style={{ stroke: 'hsl(var(--primary))', strokeWidth: 3 }}
      markerEnd={props.markerEnd}
    />
  );
}
const edgeTypes: EdgeTypes = { orange: OrangeEdge };

const defaultEdgeOptions = {
  type: 'orange',
  markerEnd: { type: MarkerType.ArrowClosed, color: 'hsl(24, 95%, 53%)', width: 22, height: 22 },
};

/** Fallback stock covers used when the AI backend is unavailable (dev bypass user, etc). */
const FALLBACK_COVERS = [
  'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&auto=format',
  'https://images.unsplash.com/photo-1577223625816-7546f13df25d?w=800&auto=format',
  'https://images.unsplash.com/photo-1487958449943-2429e8be8625?w=800&auto=format',
  'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&auto=format',
  'https://images.unsplash.com/photo-1524230572899-a752b3835840?w=800&auto=format',
];

const seedNodes: Node[] = [];

function authHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
    'X-Project-ID': PLANBASE_CONFIG.projectId,
    Authorization: `Bearer ${PLANBASE_CONFIG.apiKey}`,
    'X-User-Token': localStorage.getItem('planbase_token') || '',
  };
}

/**
 * Call the worker's aiImageRoute. Returns the generated image URL, or a
 * fallback stock cover on any auth/payment/service error so the canvas
 * demo doesn't break in dev.
 *
 * When `referenceImageUrl` is provided, the worker uses FLUX 2 with the
 * parent image as a visual reference so the child node is recognisably
 * the same building as the parent — this is the fix for chained
 * generations previously producing unrelated designs.
 */
async function generateImage(
  prompt: string,
  nodeType: PlanNodeType,
  fallbackSeed: number,
  referenceImageUrl?: string,
): Promise<string> {
  try {
    const response = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/planbase/ai/image`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        prompt,
        imageType: nodeType,
        count: 1,
        ...(referenceImageUrl ? { referenceImageUrl } : {}),
      }),
    });
    if (!response.ok) throw new Error(String(response.status));
    const data = await response.json();
    const url = Array.isArray(data.urls) ? data.urls[0] : data.url;
    if (!url) throw new Error('no-image');
    return url as string;
  } catch {
    // Fall back gracefully so the UX still demonstrates the flow.
    return FALLBACK_COVERS[fallbackSeed % FALLBACK_COVERS.length];
  }
}

function WorkflowInner() {
  const navigate = useNavigate();
  const { draftId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const rf = useReactFlow();

  // Persistence — a stable draft id lives in the URL. If there isn't one
  // yet (fresh /orbi/workflow visit), we mint one on the first meaningful
  // action and navigate to it so the URL reflects the draft.
  const { hydrated, status: saveStatus, lastSavedAt, save } = useWorkflowDraft({ draftId });

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>(seedNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [projectName, setProjectName] = useState('New File');
  const [tool, setTool] = useState<WorkflowTool>('select');
  const [zoom, setZoom] = useState(1);
  const [prompt, setPrompt] = useState('');
  const [mode, setMode] = useState<ChatComposerMode | undefined>('workflow');
  // First-time coach-mark — appears on the first successful generation
  // and points at the connector arrow so users discover the type-dropdown
  // pattern. Dismissal is remembered per browser.
  const [showConnectorCoach, setShowConnectorCoach] = useState(false);
  const nextIdRef = useRef(1);
  // Suppresses autosave-triggered hydration writes when we're just replaying
  // the draft into state at mount time.
  const hydratingRef = useRef(false);
  /**
   * The very first prompt the user typed to open this workflow ("Modern
   * 3-bedroom cottage with timber cladding" etc). Every subsequent
   * generation — chained or spawned via connector — stitches this in so
   * the AI keeps the same subject across node types.
   */
  const [rootBrief, setRootBrief] = useState<string>('');

  /** In-app image lightbox for the node Expand button. */
  const [lightbox, setLightbox] = useState<{ url: string; title?: string } | null>(null);

  /**
   * Hydrate from a saved draft when the id changes and there's stored data.
   * Strips runtime-only callback fields — we re-inject those in the callbacks
   * effect below.
   */
  useEffect(() => {
    if (!hydrated) return;
    hydratingRef.current = true;
    setNodes(
      hydrated.nodes.map((n) => {
        if (n.type === 'note') {
          const { onChange: _oc, onDelete: _od, ...rest } = (n.data || {}) as NoteNodeData & { onChange?: unknown; onDelete?: unknown };
          return { ...n, data: rest as NoteNodeData } as Node;
        }
        const {
          onSpawnChild: _1, onDelete: _2, onRetry: _3, onDownload: _4,
          onExpand: _5, onCopyImage: _6, onDuplicate: _7, onReference: _8,
          ...rest
        } = (n.data || {}) as PlanNodeData & Record<string, unknown>;
        return { ...n, data: rest as PlanNodeData } as Node;
      }),
    );
    setEdges(hydrated.edges);
    setProjectName(hydrated.title || 'New File');
    setRootBrief(hydrated.rootBrief || '');
    if (hydrated.viewport) rf.setViewport(hydrated.viewport, { duration: 0 });
    // Bump nextIdRef past anything already saved so new ids don't collide.
    const maxId = hydrated.nodes.reduce((acc, n) => {
      const m = /^n-(\d+)$/.exec(n.id);
      return m ? Math.max(acc, parseInt(m[1], 10) + 1) : acc;
    }, 1);
    nextIdRef.current = maxId;
    // Release the guard after this tick so the next state change autosaves normally.
    setTimeout(() => { hydratingRef.current = false; }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated?.id]);

  /**
   * Ensure the URL carries a draft id. When the user does something
   * meaningful on a fresh /orbi/workflow (types a prompt, drops a node),
   * mint an id and navigate — subsequent renders autosave under that id.
   */
  const ensureDraftId = useCallback((): string => {
    if (draftId) return draftId;
    const id = newDraftId();
    // Preserve any existing search params (like ?prompt=)
    const search = searchParams.toString();
    navigate(`/orbi/workflow/${id}${search ? `?${search}` : ''}`, { replace: true });
    return id;
  }, [draftId, navigate, searchParams]);

  /**
   * Autosave whenever meaningful state changes. Skipped during hydration
   * so we don't immediately re-write what we just read.
   */
  useEffect(() => {
    if (hydratingRef.current) return;
    // Nothing to save yet on a truly empty canvas without a title.
    if (!draftId && nodes.length === 0 && edges.length === 0 && !rootBrief && projectName === 'New File') return;
    const id = draftId || (nodes.length > 0 || edges.length > 0 || rootBrief ? ensureDraftId() : null);
    if (!id) return;
    // Strip runtime-only callbacks from node data before serialising.
    const serializedNodes = nodes.map((n) => {
      if (n.type === 'note') {
        const { onChange: _oc, onDelete: _od, ...rest } = (n.data || {}) as NoteNodeData & { onChange?: unknown; onDelete?: unknown };
        return { ...n, data: rest } as Node;
      }
      const {
        onSpawnChild: _1, onDelete: _2, onRetry: _3, onDownload: _4,
        onExpand: _5, onCopyImage: _6, onDuplicate: _7, onReference: _8,
        ...rest
      } = (n.data || {}) as PlanNodeData & Record<string, unknown>;
      return { ...n, data: rest } as Node;
    });
    save({
      id,
      title: projectName,
      rootBrief,
      nodes: serializedNodes,
      edges,
      viewport: rf.getViewport(),
    });
  }, [nodes, edges, projectName, rootBrief, draftId, ensureDraftId, rf, save]);

  const patchNode = useCallback(
    (id: string, patch: Partial<PlanNodeData>) => {
      setNodes((ns) =>
        ns.map((n) =>
          n.id === id ? ({ ...n, data: { ...(n.data as PlanNodeData), ...patch } } as Node) : n,
        ),
      );
    },
    [setNodes],
  );

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge(params, eds)),
    [setEdges],
  );

  const handleZoomChange = (z: number) => {
    rf.setViewport({ ...rf.getViewport(), zoom: z }, { duration: 200 });
  };

  /**
   * Create a node in loading state, kick off the AI call, and patch
   * the node when the image comes back. Returns the new node's id.
   *
   * `userPrompt` is the raw text the user typed (persisted on the node so
   * children can reference it). `subjectBrief` is what actually gets sent to
   * the AI — for a root node that's just userPrompt; for a child it's the
   * rootBrief + the child's type instruction so the AI keeps the same subject.
   */
  const spawnNode = useCallback(
    async (opts: {
      nodeType: PlanNodeType;
      userPrompt: string;
      subjectBrief: string;
      position: { x: number; y: number };
      sourceId?: string;
      /** Parent node's image — used as a visual reference to keep the same building. */
      referenceImageUrl?: string;
    }): Promise<string> => {
      const id = `n-${nextIdRef.current++}`;
      const seed = nextIdRef.current;

      const nodeData: PlanNodeData = {
        title: opts.userPrompt.slice(0, 40) || NODE_TYPE_LABEL[opts.nodeType],
        prompt: opts.userPrompt,
        subjectBrief: opts.subjectBrief,
        nodeType: opts.nodeType,
        status: 'generating',
      };
      const newNode: Node = { id, type: 'plan', position: opts.position, data: nodeData };
      setNodes((ns) => [...ns, newNode]);
      const sourceId = opts.sourceId;
      if (sourceId) {
        setEdges((es) => [
          ...es,
          { id: `e-${sourceId}-${id}`, source: sourceId, target: id, type: 'orange' } as Edge,
        ]);
      }
      rf.setCenter(opts.position.x + 110, opts.position.y + 110, {
        duration: 300,
        zoom: rf.getViewport().zoom,
      });

      try {
        const url = await generateImage(opts.subjectBrief, opts.nodeType, seed, opts.referenceImageUrl);
        patchNode(id, { status: 'idle', imageUrl: url });
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Generation failed';
        patchNode(id, { status: 'error', errorMessage: message });
      }
      return id;
    },
    [rf, setNodes, setEdges, patchNode],
  );

  /**
   * Main docked composer.
   * - If no rootBrief yet, this prompt becomes the workflow's root brief
   *   and generates the first Concept node.
   * - If a rootBrief exists, treat the input as a refinement of the last
   *   node's subject: append it to the existing brief so the AI keeps the
   *   same subject and just applies the tweak.
   * - If any node is marked as Reference, its image is used as the visual
   *   anchor for this generation regardless of which node came before it.
   */
  const handleSubmit = () => {
    const value = prompt.trim();
    if (!value) return;
    const previous = nodes[nodes.length - 1];
    const referenceNode = nodes.find(
      (n) => n.type === 'plan' && (n.data as PlanNodeData).isReference,
    );
    const offsetX = previous ? (previous.position.x as number) + 300 : 200;
    const offsetY = previous ? (previous.position.y as number) : 160;

    let subjectBrief: string;
    if (!rootBrief) {
      setRootBrief(value);
      subjectBrief = value;
    } else {
      subjectBrief = `${rootBrief}. Additional direction: ${value}`;
    }

    spawnNode({
      nodeType: 'concept',
      userPrompt: value,
      subjectBrief,
      position: { x: offsetX, y: offsetY },
      sourceId: referenceNode?.id ?? previous?.id,
      referenceImageUrl:
        referenceNode ? (referenceNode.data as PlanNodeData).imageUrl : undefined,
    });
    setPrompt('');
  };

  /**
   * Toggle a node's Reference flag. Only one node at a time carries it —
   * setting it on one clears it from any others so there's never ambiguity
   * about which image anchors the next top-level generation.
   */
  const toggleReference = useCallback(
    (id: string) => {
      setNodes((ns) => {
        const target = ns.find((n) => n.id === id);
        if (!target || target.type !== 'plan') return ns;
        const targetData = target.data as PlanNodeData;
        if (!targetData.imageUrl) {
          toast.error('Generate this node first before setting it as reference');
          return ns;
        }
        const turningOn = !targetData.isReference;
        toast.success(turningOn ? 'Set as reference' : 'Reference cleared');
        return ns.map((n) => {
          if (n.type !== 'plan') return n;
          const nd = n.data as PlanNodeData;
          const nextFlag = n.id === id ? turningOn : false;
          if (!!nd.isReference === nextFlag) return n;
          return { ...n, data: { ...nd, isReference: nextFlag } as PlanNodeData };
        });
      });
    },
    [setNodes],
  );

  /**
   * Connector-dropdown on a node → drop a new typed child.
   * Uses the parent's subjectBrief (which already reflects the whole chain's
   * design intent) plus a request for the new node type.
   */
  const spawnChild = useCallback(
    (parentId: string, nextType: PlanNodeType) => {
      const parent = nodes.find((n) => n.id === parentId);
      if (!parent) return;
      const parentData = parent.data as PlanNodeData;
      const brief = parentData.subjectBrief || parentData.prompt || rootBrief || 'architecture design';
      const typeInstruction = NODE_TYPE_LABEL[nextType].toLowerCase();
      const subjectBrief = `${brief}. Draw a ${typeInstruction} for this exact building — keep the same style, footprint, materials and design intent as any previous nodes.`;
      spawnNode({
        nodeType: nextType,
        userPrompt: `${NODE_TYPE_LABEL[nextType]} of ${parentData.title || brief.slice(0, 40)}`,
        subjectBrief,
        position: { x: (parent.position.x as number) + 300, y: parent.position.y as number },
        sourceId: parentId,
        // Passing the parent's image as a visual reference is what actually
        // makes chained generations show the SAME building — the FLUX 2 model
        // uses it as input_image_0. Without this the model has no memory
        // between calls and produces unrelated designs.
        referenceImageUrl: parentData.imageUrl,
      });
    },
    [nodes, rootBrief, spawnNode],
  );

  /**
   * Per-node mini composer → spawn a same-type sibling that inherits the
   * parent's brief with the refinement appended, using the parent image
   * as visual reference so the AI keeps continuity with the source node.
   */
  const refineNode = useCallback(
    (parentId: string, refinement: string) => {
      const parent = nodes.find((n) => n.id === parentId);
      if (!parent || parent.type !== 'plan') return;
      const parentData = parent.data as PlanNodeData;
      const brief = parentData.subjectBrief || parentData.prompt || rootBrief || 'architecture design';
      const subjectBrief = `${brief}. Additional direction: ${refinement}. Keep the same building, style, footprint and materials as the reference image.`;
      spawnNode({
        nodeType: parentData.nodeType,
        userPrompt: refinement,
        subjectBrief,
        position: { x: (parent.position.x as number) + 300, y: parent.position.y as number },
        sourceId: parentId,
        referenceImageUrl: parentData.imageUrl,
      });
    },
    [nodes, rootBrief, spawnNode],
  );

  const retryNode = useCallback(
    async (id: string) => {
      const n = nodes.find((x) => x.id === id);
      if (!n) return;
      const d = n.data as PlanNodeData;
      const brief = d.subjectBrief || d.prompt || 'architecture design';
      patchNode(id, { status: 'generating', errorMessage: undefined });
      try {
        const url = await generateImage(brief, d.nodeType, nextIdRef.current++);
        patchNode(id, { status: 'idle', imageUrl: url });
      } catch {
        patchNode(id, { status: 'error', errorMessage: 'Generation failed' });
      }
    },
    [nodes, patchNode],
  );

  const deleteNode = useCallback(
    (id: string) => {
      setNodes((ns) => ns.filter((n) => n.id !== id));
      setEdges((es) => es.filter((e) => e.source !== id && e.target !== id));
    },
    [setNodes, setEdges],
  );

  // Filename-safe title generator for downloads.
  const nodeFilename = (d: PlanNodeData) => {
    const raw = `${d.title || 'plan'}-${d.nodeType}.png`;
    return raw.toLowerCase().replace(/[^a-z0-9.-]+/g, '_');
  };

  // Inject callbacks into every node's data whenever nodes list changes.
  useEffect(() => {
    setNodes((ns) =>
      ns.map((n) => {
        // Note nodes: only wire their own change/delete callbacks.
        if (n.type === 'note') {
          const noteData = n.data as NoteNodeData;
          return {
            ...n,
            data: {
              ...noteData,
              onChange: (text: string) => patchNode(n.id, { text } as any),
              onDelete: () => deleteNode(n.id),
            } as NoteNodeData,
          } as Node;
        }

        const d = n.data as PlanNodeData;
        return {
          ...n,
          data: {
            ...d,
            onSpawnChild: (t: PlanNodeType) => spawnChild(n.id, t),
            onRefine: (text: string) => refineNode(n.id, text),
            onDelete: () => deleteNode(n.id),
            onRetry: () => retryNode(n.id),
            onDownload: () => {
              if (d.imageUrl) downloadImage(d.imageUrl, nodeFilename(d));
            },
            onExpand: () => {
              if (d.imageUrl) setLightbox({ url: d.imageUrl, title: d.title });
            },
            onCopyImage: () => {
              if (d.imageUrl) {
                navigator.clipboard.writeText(d.imageUrl);
                toast.success('Image URL copied');
              }
            },
            onDuplicate: () => {
              spawnNode({
                nodeType: d.nodeType,
                userPrompt: d.prompt || d.title || 'architecture design',
                subjectBrief: d.subjectBrief || d.prompt || 'architecture design',
                position: { x: (n.position.x as number) + 60, y: (n.position.y as number) + 60 },
              });
            },
            onReference: () => toggleReference(n.id),
          } as PlanNodeData,
        } as Node;
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes.length, spawnChild, refineNode, deleteNode, retryNode, spawnNode, toggleReference]);

  // Seed from ?prompt=... (arriving from /orbi with Workflow mode).
  useEffect(() => {
    const seedPrompt = searchParams.get('prompt');
    if (seedPrompt && nodes.length === 0) {
      setRootBrief(seedPrompt);
      spawnNode({
        nodeType: 'concept',
        userPrompt: seedPrompt,
        subjectBrief: seedPrompt,
        position: { x: 200, y: 160 },
      });
      searchParams.delete('prompt');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Publish flow
  const handlePublish = async (visibility: PlanverseVisibility) => {
    const nodeWithImage = [...nodes].reverse().find((n) => (n.data as PlanNodeData).imageUrl);
    if (!nodeWithImage) {
      toast.error('Generate at least one plan before publishing');
      return;
    }
    const cover = (nodeWithImage.data as PlanNodeData).imageUrl!;
    const plan = await publishPlan({
      title: projectName || 'Untitled Workflow',
      coverUrl: cover,
      authorName: user?.name || 'Anonymous',
      authorUsername: user?.metadata?.username || 'you',
      visibility,
      tags: ['workflow'],
      category: 'workflow',
    });
    const link = `${window.location.origin}/planverse/${plan.id}`;
    if (visibility === 'public') {
      toast.success('Published to Planverse', {
        description: 'Anyone on Planbase can now find and duplicate this plan.',
        action: { label: 'View', onClick: () => navigate(`/planverse/${plan.id}`) },
      });
    } else {
      await navigator.clipboard.writeText(link).catch(() => {});
      toast.success('Link copied', {
        description: 'Anyone with this link can view it, but it’s not on the Planverse feed.',
      });
    }
  };

  const handleGetLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).catch(() => {});
    toast.success('Workflow link copied');
  };

  /**
   * Comment tool: click empty canvas → drop a sticky-note node at the
   * click position (converted from screen coords to flow coords).
   */
  const handlePaneClick = useCallback(
    (event: React.MouseEvent) => {
      if (tool !== 'comment') return;
      const position = rf.screenToFlowPosition({ x: event.clientX, y: event.clientY });
      const id = `note-${Date.now()}`;
      const newNode: Node = {
        id,
        type: 'note',
        position,
        data: { text: '' } as NoteNodeData,
      };
      setNodes((ns) => [...ns, newNode]);
    },
    [tool, rf, setNodes],
  );

  /**
   * Export the entire canvas as a single PNG.
   *
   * Uses xyflow's node bounds to compute a viewport that fits every node,
   * then rasterises the ReactFlow viewport DOM at 2× scale via html-to-image.
   * Cross-origin plan images (Cloudflare AI URLs) are already CORS-friendly
   * so `useCORS`-style opt-in isn't needed — html-to-image inlines them
   * through canvas.
   */
  const exportCanvasAsPng = useCallback(async () => {
    const planNodes = nodes.filter((n) => n.type === 'plan' || n.type === 'note');
    if (planNodes.length === 0) {
      toast.error('Nothing on the canvas yet');
      return;
    }

    const viewportEl = document.querySelector('.react-flow__viewport') as HTMLElement | null;
    if (!viewportEl) {
      toast.error("Couldn't find canvas");
      return;
    }

    const toastId = toast.loading('Rendering canvas…');
    try {
      const bounds = getNodesBounds(planNodes);
      const padding = 80;
      const imageWidth = 1600;
      const imageHeight = Math.round(
        imageWidth * ((bounds.height + padding * 2) / (bounds.width + padding * 2)),
      );
      const vp = getViewportForBounds(bounds, imageWidth, imageHeight, 0.5, 2, padding);

      const dataUrl = await toPng(viewportEl, {
        backgroundColor: '#ffffff',
        width: imageWidth,
        height: imageHeight,
        pixelRatio: 2,
        cacheBust: true,
        style: {
          width: `${imageWidth}px`,
          height: `${imageHeight}px`,
          transform: `translate(${vp.x}px, ${vp.y}px) scale(${vp.zoom})`,
        },
        // Skip minimap / controls / attribution — they're overlays outside
        // the viewport but html-to-image can still catch them via portals.
        filter: (node) => {
          if (!(node instanceof HTMLElement)) return true;
          const cls = node.className;
          const s = typeof cls === 'string' ? cls : '';
          return !(
            s.includes('react-flow__minimap') ||
            s.includes('react-flow__controls') ||
            s.includes('react-flow__attribution')
          );
        },
      });

      const safeName = projectName.trim().toLowerCase().replace(/[^a-z0-9.-]+/g, '_') || 'workflow';
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `${safeName}.png`;
      link.click();
      toast.success('Canvas exported', { id: toastId });
    } catch (err) {
      console.error('Canvas export failed', err);
      toast.error('Export failed — try again', { id: toastId });
    }
  }, [nodes, projectName]);

  /**
   * Toolbar Download tool: not a persistent mode — instantly exports the
   * whole canvas as a PNG, then leaves the tool where it was.
   */
  const handleToolChange = (next: WorkflowTool) => {
    if (next === 'download') {
      void exportCanvasAsPng();
      return;
    }
    setTool(next);
  };

  /**
   * Keyboard shortcuts — V select, H pan, C comment, ⌘E export. Skips
   * events when the user is typing in an input/textarea/contenteditable
   * so shortcuts never eat characters. Matches the shortcut chip labels
   * shown in WorkflowCanvasTools.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return;
      }
      // ⌘E / Ctrl+E → export
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'e') {
        e.preventDefault();
        void exportCanvasAsPng();
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === 'v') { e.preventDefault(); setTool('select'); }
      else if (k === 'h') { e.preventDefault(); setTool('pan'); }
      else if (k === 'c') { e.preventDefault(); setTool('comment'); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [exportCanvasAsPng]);

  /**
   * Coach-mark trigger — fires once when the user lands their first
   * completed plan node. Skipped for anyone who's dismissed it before,
   * and never fires while hydrating a saved draft (those nodes came
   * from a previous session).
   */
  useEffect(() => {
    if (hydratingRef.current) return;
    if (showConnectorCoach) return;
    const dismissed = localStorage.getItem('planbase_workflow_coach_dismissed');
    if (dismissed) return;
    const firstIdle = nodes.find(
      (n) => n.type === 'plan' && (n.data as PlanNodeData).status === 'idle' && !!(n.data as PlanNodeData).imageUrl,
    );
    if (firstIdle) {
      setShowConnectorCoach(true);
      const t = setTimeout(() => {
        setShowConnectorCoach(false);
        localStorage.setItem('planbase_workflow_coach_dismissed', '1');
      }, 15000);
      return () => clearTimeout(t);
    }
  }, [nodes, showConnectorCoach]);

  const dismissCoach = useCallback(() => {
    setShowConnectorCoach(false);
    localStorage.setItem('planbase_workflow_coach_dismissed', '1');
  }, []);

  const userInitial = (user?.name?.[0] || user?.email?.[0] || 'T').toUpperCase();

  return (
    <div className="flex h-full w-full flex-col">
      <WorkflowHeader
        projectName={projectName}
        onProjectNameChange={setProjectName}
        zoom={zoom}
        onZoomChange={handleZoomChange}
        onExportToPlanverse={() => handlePublish('public')}
        onPublishUnlisted={() => handlePublish('unlisted')}
        onGetLink={handleGetLink}
        userInitial={userInitial}
        saveStatus={saveStatus}
        lastSavedAt={lastSavedAt}
      />

      <div className="relative flex-1 overflow-hidden">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onMove={(_, viewport) => setZoom(viewport.zoom)}
          onPaneClick={handlePaneClick}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          defaultEdgeOptions={defaultEdgeOptions}
          // Middle-mouse (1) and right-click (2) pan in every mode so users
          // aren't trapped by whichever primary tool is active. Comment
          // mode also allows primary-button drag to pan so a stray drag
          // doesn't drop a note.
          panOnDrag={tool === 'pan' ? [0, 1, 2] : [1, 2]}
          selectionOnDrag={tool === 'select'}
          defaultViewport={{ x: 100, y: 60, zoom: 1 }}
          minZoom={0.25}
          maxZoom={2}
          proOptions={{ hideAttribution: true }}
          className={tool === 'comment' ? 'cursor-crosshair' : ''}
        >
          <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="hsl(var(--border))" />
        </ReactFlow>

        <WorkflowCanvasTools
          active={tool}
          onChange={handleToolChange}
          className="absolute left-4 top-1/2 -translate-y-1/2"
        />

        {lightbox && (
          <PlanImageLightbox
            imageUrl={lightbox.url}
            title={lightbox.title}
            onClose={() => setLightbox(null)}
            onDownload={() => downloadImage(lightbox.url, (lightbox.title || 'plan') + '.png')}
          />
        )}

        {/* Empty-canvas hint */}
        {nodes.length === 0 && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="max-w-sm text-center font-display text-muted-foreground">
              <p className="text-base">Describe the first design below.</p>
              <p className="mt-1 text-sm">Orbi will generate a concept image, then click the connector to spin off elevations, floor plans and more.</p>
            </div>
          </div>
        )}

        {/*
          First-time coach — appears once after the user's first
          generated plan lands. Points at the connector button pattern
          (top-right of every plan node) since that's the least
          discoverable interaction on the canvas.
        */}
        {showConnectorCoach && (
          <div
            className="pointer-events-auto absolute right-6 top-6 z-30 w-72 rounded-2xl border border-primary/40 bg-background p-4 font-display shadow-xl"
            role="dialog"
            aria-labelledby="coach-title"
          >
            <div className="flex items-start justify-between gap-2">
              <p id="coach-title" className="text-sm font-semibold text-foreground">
                Spin off related drawings
              </p>
              <button
                type="button"
                onClick={dismissCoach}
                aria-label="Dismiss"
                className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              Click the small arrow on the right edge of a plan node to spin off an elevation, section, or floor plan that keeps the same building.
            </p>
            <div className="mt-3 flex items-center justify-between text-[10px] uppercase tracking-wide">
              <span className="text-muted-foreground">Tips</span>
              <span className="font-mono text-muted-foreground">V · H · C · ⌘E</span>
            </div>
          </div>
        )}

        <div className="pointer-events-none absolute inset-x-0 bottom-6 px-4">
          <div className="pointer-events-auto mx-auto max-w-3xl">
            <ChatComposer
              value={prompt}
              onChange={setPrompt}
              onSubmit={handleSubmit}
              mode={mode}
              onModeChange={setMode}
              placeholder="Describe a design or refine the last node…"
            />
          </div>
        </div>
      </div>

      {draftId && <span className="hidden">{draftId}</span>}
    </div>
  );
}

export default function Workflow() {
  return (
    <ReactFlowProvider>
      <WorkflowInner />
    </ReactFlowProvider>
  );
}
